import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import moment from "moment";
import { BiFootball } from "react-icons/bi";
import { GiRunningShoe } from "react-icons/gi";
import { useNavigate } from "react-router-dom";
import bettingService from "../../../api/services/bettingService";

const SelectedMatchesCard = ({ match }) => {
  const { t, i18n } = useTranslation();
  const language = i18n.language || "en";
  const [data, setData] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    if (!match?.gamblingId || match?.gamblingTypeId !== 1) return;

    const fetchData = async () => {
      try {
        setIsLoading(true);
        const res = await bettingService.bettingHistoryDetails({
          gamblingId: Number(match.gamblingId),
          status: "Inplay",
        });

        // If bettingService already returns parsed JSON, remove .json()
        const responseData = Array.isArray(res) ? res : await res.json();

        if (responseData?.length > 0) {
          setData({ ...match, ...responseData[0] });
        }
      } catch (err) {
        if (err?.response?.status === 401) {
          navigate("/login");
        } else {
          console.error("API request error:", err);
        }
      } finally {
        setIsLoading(false);
      }
    };

    setData(null);
    fetchData();
  }, [match?.gamblingId, match?.gamblingTypeId, navigate]);

  return (
    <div className="flex flex-col p-3 mx-2 my-2 transition-all duration-300 border shadow-md rounded-xl bg-gradient-to-r from-white/95 via-white/98 to-white/95 backdrop-blur-xl border-crimson/30 font-myanmarSansPro hover:border-crimson/50 hover:shadow-lg">
      {/* Header */}
      <div className="flex items-center justify-between px-2">
        <div className="flex items-center gap-1.5">
          <div className="flex items-center justify-center w-6 h-6 border rounded-md shadow-sm bg-gradient-to-br from-crimson via-red-primary to-crimson border-golden/40">
            {match?.gamblingTypeId === 1 ? (
              <BiFootball size={12} className="text-white" />
            ) : (
              <GiRunningShoe size={12} className="text-white" />
            )}
          </div>
          <h2 className="text-sm font-semibold text-blue-primary">
            {match?.gamblingTypeId === 1 ? t("body") : t("maungg")}
          </h2>
        </div>

        <p className="text-[10px] text-blue-primary bg-white border border-crimson/30 px-2 py-1 rounded-md font-dangrek">
          {moment(match?.createdDate).format("DD.MM.YYYY h:mm A")}
        </p>
      </div>

      {/* Bet Details */}
      <div className="px-3 mt-2">
        <div className="grid grid-cols-2 gap-2 py-1 pl-4 mx-1 text-xs border rounded-lg bg-white/80 border-crimson/20">
          <h4 className="font-semibold text-blue-primary font-dangrek">
            BetID
          </h4>
          <p className="px-1 py-0.5 font-mono text-[11px] rounded bg-crimson/5 text-blue-primary">
            {match?.postingNo}
          </p>
        </div>

        <div className="grid grid-cols-2 gap-3 py-2 pl-4 mx-1 mt-2 text-sm border rounded-lg bg-white/80 border-crimson/20">
          <h4
            className={`text-blue-primary ${
              language === "en" ? "" : "font-bold"
            }`}
          >
            {t("betAmount")}
          </h4>
          <p className="text-base font-bold font-dangrek text-blue-primary">
            {parseInt(match?.amount || 0).toLocaleString()}
          </p>
        </div>

        {match?.gamblingTypeId === 2 && (
          <div className="grid grid-cols-2 gap-3 py-2 pl-4 mx-1 mt-2 text-sm border rounded-lg bg-white/80 border-crimson/20">
            <h4
              className={`text-blue-primary ${
                language === "en" ? "" : "font-bold"
              }`}
            >
              {t("teamCount")}
            </h4>
            <p className="font-bold font-dangrek text-blue-primary">
              {match?.teamCount}
            </p>
          </div>
        )}

        <div className="flex justify-end mt-2">
          {match?.status === "Inplay" && (
            <span className="bg-gradient-to-br from-crimson via-red-primary to-crimson text-white px-2 py-0.5 rounded-md text-xs font-dangrek font-semibold shadow-sm border border-golden/40">
              Ongoing
            </span>
          )}
        </div>

        {isLoading && (
          <div className="w-4 h-4 ml-4 border-t-4 border-l-2 rounded-full border-crimson animate-spin" />
        )}

        {/* Football Betting Details */}
        {data?.gamblingTypeId === 1 && (
          <div className="relative px-5 py-2 mt-3 text-sm border rounded-lg bg-white/90 backdrop-blur-sm border-crimson/20">
            <div className="absolute top-0 left-0 right-0 h-[3px] bg-gradient-to-r from-crimson via-golden to-crimson rounded-t-lg"></div>

            <div className="flex justify-between mt-1">
              <div>
                <p
                  className={`${
                    data?.isHomeBodyOdd
                      ? "text-blue-primary font-semibold"
                      : "text-blue-primary"
                  }`}
                >
                  {data?.isHome
                    ? language === "en"
                      ? data?.bettedTeam
                      : data?.bettedTeamMyan
                    : language === "en"
                    ? data?.oppositeTeamName
                    : data?.oppositeTeamNameMyan}
                </p>

                <p
                  className={`mt-1 ${
                    !data?.isHomeBodyOdd
                      ? "text-blue-primary font-semibold"
                      : "text-blue-primary"
                  }`}
                >
                  {!data?.isHome
                    ? language === "en"
                      ? data?.bettedTeam
                      : data?.bettedTeamMyan
                    : language === "en"
                    ? data?.oppositeTeamName
                    : data?.oppositeTeamNameMyan}
                </p>
              </div>

              <div className="text-right font-dangrek">
                <p className="text-sm font-bold text-blue-primary">
                  « {data?.home || data?.away ? data?.bodyOdd : data?.goalOdd}
                </p>
              </div>
            </div>

            <div className="flex items-center justify-center mt-2 space-x-2 font-dangrek">
              <p className="px-2 py-0.5 text-sm font-bold border rounded text-blue-primary bg-crimson/10 border-crimson/20">
                {data?.homeResult}
              </p>
              <BiFootball className="text-crimson" size={14} />
              <p className="px-2 py-0.5 text-sm font-bold border rounded text-blue-primary bg-crimson/10 border-crimson/20">
                {data?.awayResult}
              </p>
            </div>

            <div className="grid grid-cols-2 gap-6 pt-3 mt-2 text-sm border-t border-crimson/20">
              <h4
                className={`text-blue-primary ${
                  language === "en" ? "" : "font-bold"
                }`}
              >
                {t("choice")}
              </h4>
              <p className="px-1 py-0.5 rounded bg-crimson/10 border border-crimson/20 text-blue-primary">
                {data?.home || data?.away
                  ? language === "en"
                    ? data?.bettedTeam
                    : data?.bettedTeamMyan
                  : data?.overs
                  ? t("goalOver")
                  : data?.under
                  ? t("goalUnder")
                  : ""}
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default SelectedMatchesCard;
